import {
  Dialog,
  DialogContent,
  DialogTitle, // Accessibility requirement often
} from "@/components/ui/dialog";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
  type CarouselApi,
} from "@/components/ui/carousel";
import Image from "next/image";
import { useEffect, useState } from "react";
import { X } from "lucide-react";

interface WorkLightboxProps {
  images: string[];
  initialIndex: number;
  isOpen: boolean;
  onClose: () => void;
}

export function WorkLightbox({
  images,
  initialIndex,
  isOpen,
  onClose,
}: WorkLightboxProps) {
  const [api, setApi] = useState<CarouselApi>();

  useEffect(() => {
    if (api && isOpen) {
      api.scrollTo(initialIndex, true);
    }
  }, [api, isOpen, initialIndex]);

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      {/* 
        DialogContent usually has a background and max-width.
        We override it to be transparent and larger.
        We hide the default close button to position our own better.
      */}
      <DialogContent
        className="max-w-[95vw] w-full h-[95vh] bg-transparent border-none shadow-none p-0 flex flex-col items-center justify-center outline-none"
        showCloseButton={false}
      >
        <div className="sr-only">
          <DialogTitle>Bildergalerie</DialogTitle>
        </div>

        <div className="relative w-full h-full">
          <button
            onClick={onClose}
            className="absolute top-4 right-4 z-50 p-2 text-white/80 hover:text-white bg-black/40 hover:bg-black/60 rounded-full transition-colors"
            aria-label="Schließen"
          >
            <X className="w-8 h-8" />
          </button>

          <Carousel
            setApi={setApi}
            className="w-full h-full lightbox-carousel"
            opts={{
              loop: true,
              align: "center",
            }}
          >
            {/* 
              In Shadcn/embla, CarouselContent is wrapped in an overflow-hidden div (the viewport).
              We need THAT div to be full height. 
           */}
            <style jsx global>{`
              .lightbox-carousel [data-slot="carousel-content"] {
                height: 100%;
              }
            `}</style>
            <CarouselContent className="h-full ml-0">
              {images.map((src, index) => (
                <CarouselItem key={index} className="h-full pl-0 min-w-0">
                  <div className="relative w-full h-full flex items-center justify-center">
                    <div className="relative w-full h-full max-h-[90vh] max-w-[90vw]">
                      <Image
                        src={src}
                        alt={`Detail view ${index + 1}`}
                        fill
                        className="object-contain"
                        priority={index === initialIndex}
                        sizes="95vw"
                      />
                    </div>
                  </div>
                </CarouselItem>
              ))}
            </CarouselContent>
            <CarouselPrevious className="cursor-pointer left-1 md:-left-14 bg-black/50 hover:bg-black/60 border-none text-white hover:text-white h-9 w-9 md:h-12 md:w-12 backdrop-blur-sm" />
            <CarouselNext className="cursor-pointer right-1 md:-right-14 bg-black/50 hover:bg-black/60 border-none text-white hover:text-white h-9 w-9 md:h-12 md:w-12 backdrop-blur-sm" />
          </Carousel>
        </div>
      </DialogContent>
    </Dialog>
  );
}
